import { useState, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import {
  CREATE_WHATSAPP_TEMPLATE,
  UPLOAD_MEDIA,
  LIST_WHATSAPP_TEMPLATES,
} from '~/services/campaigns';
// import { LIST_MEDIA } from '~/services/media'; // TODO: Add media service
import {
  ArrowLeftIcon,
  PlusIcon,
  PhotoIcon,
  VideoCameraIcon,
  DocumentTextIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  PaperClipIcon,
  PlayIcon,
  EyeIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useLayoutContext } from '~/components/layout/Layout';
import { WhatsAppPreview } from './WhatsAppPreview';
import { convertHtmlToWhatsApp } from '~/lib/whatsapp-formatter';

interface Variable {
  id: string;
  name: string;
  position: number;
  example: string;
  component_type: 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS';
}

interface TemplateComponent {
  type: 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS';
  enabled: boolean;
  // Header specific
  format?: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
  text?: string;
  media?: any;
  // Body specific
  bodyText?: string;
  // Footer specific
  footerText?: string;
  // Buttons specific
  buttons?: Array<{
    id: string;
    type: 'QUICK_REPLY' | 'URL';
    text: string;
    url?: string;
  }>;
}

interface TemplateData {
  // Basic info
  templateName: string;
  category: 'UTILITY' | 'MARKETING' | 'AUTHENTICATION';
  language: string;

  // Components
  components: TemplateComponent[];

  // Variables
  variables: Variable[];
}

interface CreateTemplateModuleProps {
  agentId: string;
  channel: any;
}

const MediaUploader = ({
  onMediaUploaded,
  acceptedTypes,
  maxFileSize = 16 * 1024 * 1024, // 16MB default
  agentId,
  channelId,
  uploadedMedia,
}: {
  onMediaUploaded: (media: any) => void;
  acceptedTypes: string[];
  maxFileSize?: number;
  agentId: string;
  channelId: string;
  uploadedMedia?: any;
}) => {
  const [uploading, setUploading] = useState(false);
  const { renderToast } = useLayoutContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation('campaigns');

  const [uploadMedia] = useMutation(UPLOAD_MEDIA);

  const getFileTypeIcon = (type: string) => {
    if (type.startsWith('image/')) return <PhotoIcon className="h-5 w-5" />;
    if (type.startsWith('video/'))
      return <VideoCameraIcon className="h-5 w-5" />;
    return <DocumentTextIcon className="h-5 w-5" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFileSelect = async (file: File) => {
    if (!acceptedTypes.includes(file.type)) {
      renderToast({
        message: t('unsupported_file_type', { type: file.type }),
        type: 'error',
      });
      return;
    }

    if (file.size > maxFileSize) {
      renderToast({
        message: t('file_too_large', { maxSize: formatFileSize(maxFileSize) }),
        type: 'error',
      });
      return;
    }

    try {
      setUploading(true);

      // First upload the file using the REST API
      const formData = new FormData();
      formData.append('file', file);
      formData.append('agent_id', agentId);
      formData.append('description', file.name);

      const uploadResponse = await fetch(
        `${import.meta.env.VITE_API_URL}/files/upload`,
        {
          method: 'POST',
          body: formData,
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        },
      );

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.statusText}`);
      }

      const uploadResult = await uploadResponse.json();

      if (uploadResult.hasError) {
        throw new Error(uploadResult.message || t('upload_error'));
      }

      // Now call the UploadMedia mutation with the correct parameters
      const mediaType = file.type.startsWith('image/')
        ? 'IMAGE'
        : file.type.startsWith('video/')
          ? 'VIDEO'
          : file.type.startsWith('audio/')
            ? 'AUDIO'
            : 'DOCUMENT';

      // Construct full URL from the upload response
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      const fullUrl = `${baseUrl}/media/${uploadResult.data.url}`;

      const result = await uploadMedia({
        variables: {
          data: {
            agent_id: agentId,
            channel_id: channelId,
            name: file.name,
            type: mediaType,
            url: fullUrl, // Now this is a proper URL
            alt_description: file.name,
            for_template: true,
            validate_for_meta: true,
          },
        },
      });

      if (result.data?.uploadMedia?.hasError) {
        renderToast({
          message: result.data.uploadMedia.message || t('processing_error'),
          type: 'error',
        });
      } else {
        const uploadedMedia = JSON.parse(
          result.data?.uploadMedia?.data || '{}',
        );
        onMediaUploaded(uploadedMedia);
        renderToast({
          message: t('upload_success'),
          type: 'success',
        });
      }
    } catch (error: any) {
      renderToast({
        message: t('upload_error_message', { error: error.message }),
        type: 'error',
      });
    } finally {
      setUploading(false);
    }
  };

  // If media is already uploaded, don't show the uploader
  if (uploadedMedia) {
    return null;
  }

  return (
    <div className="space-y-4">
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes.join(',')}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            handleFileSelect(file);
          }
        }}
        className="hidden"
      />

      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="border-base-300 hover:bg-base-50 flex w-full items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
      >
        <div className="text-center">
          {uploading ? (
            <>
              <div className="loading loading-spinner loading-md mx-auto mb-2"></div>
              <p className="text-base-content/60 text-sm">{t('uploading')}</p>
            </>
          ) : (
            <>
              <PlusIcon className="text-base-content/40 mx-auto mb-2 h-8 w-8" />
              <p className="text-base-content text-sm font-medium">
                {t('click_to_upload')}
              </p>
              <p className="text-base-content/60 text-xs">
                {t('accepted_types')}: {acceptedTypes.join(', ')}
              </p>
              <p className="text-base-content/60 text-xs">
                {t('max_size')}: {formatFileSize(maxFileSize)}
              </p>
            </>
          )}
        </div>
      </button>
    </div>
  );
};

const MediaPreview = ({
  media,
  onRemove,
}: {
  media: any;
  onRemove: () => void;
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const { t } = useTranslation('campaigns');

  if (!media) return null;

  const isImage =
    media.mime_type?.startsWith('image/') || media.type === 'IMAGE';
  const isVideo =
    media.mime_type?.startsWith('video/') || media.type === 'VIDEO';
  const isDocument = !isImage && !isVideo;

  const formatFileSize = (bytes: number) => {
    if (!bytes) return 'Unknown size';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="border-base-200 bg-base-100 relative overflow-hidden rounded-lg border">
      {/* Media Preview Section */}
      <div className="relative">
        {isImage && (
          <div className="bg-base-200 relative aspect-video">
            <img
              src={media.url}
              alt={media.filename || media.name}
              className="h-full w-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement?.classList.add(
                  'flex',
                  'items-center',
                  'justify-center',
                );
                if (e.currentTarget.parentElement) {
                  e.currentTarget.parentElement.innerHTML = `
                    <div class="text-center">
                      <svg class="h-12 w-12 text-base-content/40 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                      <p class="text-sm text-base-content/60">${t(
                        'image_preview_error',
                      )}</p>
                    </div>
                  `;
                }
              }}
            />
            <button
              onClick={() => setShowPreview(true)}
              className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 opacity-0 transition-all duration-200 hover:bg-opacity-20 hover:opacity-100"
            >
              <div className="rounded-full bg-black bg-opacity-60 p-3">
                <EyeIcon className="h-6 w-6 text-white" />
              </div>
            </button>
          </div>
        )}

        {isVideo && (
          <div className="bg-base-300 relative flex aspect-video items-center justify-center">
            {/* Video thumbnail or placeholder */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-600 opacity-20"></div>
            <div className="relative z-10 text-center">
              <div className="mb-3 inline-flex rounded-full bg-black bg-opacity-60 p-4">
                <PlayIcon className="h-8 w-8 text-white" />
              </div>
              <p className="text-base-content text-sm font-medium">
                {t('video_ready')}
              </p>
              <p className="text-base-content/60 text-xs">
                {t('click_to_preview')}
              </p>
            </div>
            <button
              onClick={() => setShowPreview(true)}
              className="absolute inset-0 transition-all duration-200 hover:bg-black hover:bg-opacity-10"
            />
          </div>
        )}

        {isDocument && (
          <div className="bg-base-200 flex aspect-video items-center justify-center">
            <div className="text-center">
              <DocumentTextIcon className="text-base-content/40 mx-auto mb-2 h-12 w-12" />
              <p className="text-base-content text-sm font-medium">
                {t('document_uploaded')}
              </p>
              <p className="text-base-content/60 text-xs">
                {media.filename || media.name}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Media Info Section */}
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex min-w-0 flex-1 items-start space-x-3">
            <div className="text-base-content/60 mt-1 flex-shrink-0">
              {isImage && <PhotoIcon className="h-5 w-5" />}
              {isVideo && <VideoCameraIcon className="h-5 w-5" />}
              {isDocument && <DocumentTextIcon className="h-5 w-5" />}
            </div>

            <div className="min-w-0 flex-1">
              <p className="text-base-content truncate text-sm font-medium">
                {media.filename || media.name || t('uploaded_file')}
              </p>
              <div className="mt-1 flex items-center space-x-2">
                <p className="text-base-content/60 text-xs">
                  {media.mime_type || 'Unknown type'}
                </p>
                {media.file_size && (
                  <>
                    <span className="text-base-content/40">•</span>
                    <p className="text-base-content/60 text-xs">
                      {formatFileSize(media.file_size)}
                    </p>
                  </>
                )}
              </div>
              {media.meta_info?.file_handle && (
                <div className="mt-2 flex items-center">
                  <div className="bg-success/10 text-success flex items-center space-x-1 rounded-full px-2 py-1">
                    <div className="bg-success h-2 w-2 rounded-full"></div>
                    <span className="text-xs font-medium">
                      {t('meta_uploaded')}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <button
            onClick={onRemove}
            className="text-error hover:text-error/80 hover:bg-error/10 ml-2 flex-shrink-0 rounded-lg p-2 transition-all duration-200"
            title={t('remove_media')}
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Action Buttons */}
        <div className="mt-3 flex space-x-2">
          {(isImage || isVideo) && (
            <button
              onClick={() => setShowPreview(true)}
              className="btn btn-outline btn-sm flex-1"
            >
              <EyeIcon className="mr-1 h-4 w-4" />
              {t('preview')}
            </button>
          )}
          <button
            onClick={onRemove}
            className="btn btn-error btn-outline btn-sm"
          >
            <TrashIcon className="mr-1 h-4 w-4" />
            {t('remove')}
          </button>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4">
          <div className="relative max-h-screen w-full max-w-screen-lg">
            <button
              onClick={() => setShowPreview(false)}
              className="bg-base-100 text-base-content hover:bg-base-200 absolute -right-2 -top-2 z-10 rounded-full p-2 shadow-lg transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>

            <div className="bg-base-100 overflow-hidden rounded-lg shadow-2xl">
              <div className="bg-base-200 border-base-300 border-b px-4 py-3">
                <h3 className="text-base-content truncate font-medium">
                  {media.filename || media.name || t('media_preview')}
                </h3>
              </div>

              <div className="relative">
                {isImage && (
                  <img
                    src={media.url}
                    alt={media.filename || media.name}
                    className="mx-auto block max-h-[70vh] max-w-full"
                  />
                )}
                {isVideo && (
                  <video
                    src={media.url}
                    controls
                    className="mx-auto block max-h-[70vh] max-w-full"
                    preload="metadata"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Variable Manager Component
const VariableManager = ({
  variables,
  onVariablesChange,
  selectedComponent,
  t,
}: {
  variables: Variable[];
  onVariablesChange: (variables: Variable[]) => void;
  selectedComponent: 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS';
  t: (key: string, params?: any) => string;
}) => {
  const componentVariables = variables.filter(
    (v) => v.component_type === selectedComponent,
  );

  const addVariable = () => {
    const newPosition = Math.max(0, ...variables.map((v) => v.position)) + 1;
    const newVariable: Variable = {
      id: Date.now().toString(),
      name: '',
      position: newPosition,
      example: '',
      component_type: selectedComponent,
    };
    onVariablesChange([...variables, newVariable]);
  };

  const updateVariable = (
    id: string,
    field: keyof Variable,
    value: string | number,
  ) => {
    onVariablesChange(
      variables.map((v) => (v.id === id ? { ...v, [field]: value } : v)),
    );
  };

  const removeVariable = (id: string) => {
    onVariablesChange(variables.filter((v) => v.id !== id));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-base-content text-sm font-medium">
          {t('variables_for_component', { component: selectedComponent })}
        </h4>
        <button
          onClick={addVariable}
          className="text-custom-primary-accent-600 hover:text-custom-primary-accent-700 text-sm"
        >
          + {t('add_variable')}
        </button>
      </div>

      {componentVariables.map((variable) => (
        <div
          key={variable.id}
          className="border-custom-surface-200 space-y-2 rounded-lg border p-3"
        >
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">{`{${variable.position}}`}</span>
            <button
              onClick={() => removeVariable(variable.id)}
              className="text-red-500 hover:text-red-700"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
          <input
            type="text"
            value={variable.name}
            onChange={(e) =>
              updateVariable(variable.id, 'name', e.target.value)
            }
            placeholder={t('variable_name_placeholder')}
            className="border-base-300 focus:border-custom-primary-accent-600 w-full rounded border px-2 py-1 text-xs focus:outline-none"
          />
          <input
            type="text"
            value={variable.example}
            onChange={(e) =>
              updateVariable(variable.id, 'example', e.target.value)
            }
            placeholder={t('variable_example_placeholder')}
            className="border-base-300 focus:border-custom-primary-accent-600 w-full rounded border px-2 py-1 text-xs focus:outline-none"
          />
        </div>
      ))}
    </div>
  );
};

// Component Editor
const ComponentEditor = ({
  component,
  onComponentChange,
  variables,
  onVariablesChange,
  agentId,
  channelId,
  setActiveTab,
  t,
}: {
  component: TemplateComponent;
  onComponentChange: (component: TemplateComponent) => void;
  variables: Variable[];
  onVariablesChange: (variables: Variable[]) => void;
  agentId: string;
  channelId: string;
  setActiveTab: (tab: 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS') => void;
  t: (key: string, params?: any) => string;
}) => {
  const quillRef = useRef<ReactQuill>(null);

  const componentVariables = variables.filter(
    (v) => v.component_type === component.type,
  );

  // Quill modules for rich text editing
  const modules = useMemo(
    () => ({
      toolbar: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['clean'],
      ],
    }),
    [],
  );

  const formats = ['bold', 'italic', 'underline', 'list', 'bullet'];

  const insertVariable = (position: number) => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const range = quill.getSelection();
      if (range) {
        quill.insertText(range.index, `{{${position}}}`, 'user');
      }
    }
  };

  switch (component.type) {
    case 'HEADER':
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base-content text-lg font-medium">
              {t('component_header')}
            </h3>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={component.enabled}
                onChange={(e) =>
                  onComponentChange({ ...component, enabled: e.target.checked })
                }
                className="border-base-300 text-custom-primary-accent-600 focus:ring-custom-primary-accent-600 rounded"
              />
              <span className="text-base-content ml-2 text-sm">
                {t('enable')}
              </span>
            </label>
          </div>

          {component.enabled && (
            <>
              <div>
                <label className="text-base-content mb-2 block text-sm font-medium">
                  {t('header_type')}
                </label>
                <select
                  value={component.format || 'TEXT'}
                  onChange={(e) =>
                    onComponentChange({
                      ...component,
                      format: e.target.value as any,
                    })
                  }
                  className="border-base-300 focus:border-custom-primary-accent-600 focus:ring-custom-primary-accent-600 w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-1"
                >
                  <option value="TEXT">{t('format_text')}</option>
                  <option value="IMAGE">{t('format_image')}</option>
                  <option value="VIDEO">{t('format_video')}</option>
                  <option value="DOCUMENT">{t('format_document')}</option>
                </select>
              </div>

              {component.format === 'TEXT' && (
                <div>
                  <label className="text-base-content mb-2 block text-sm font-medium">
                    {t('header_text')}
                  </label>
                  <input
                    type="text"
                    value={component.text || ''}
                    onChange={(e) =>
                      onComponentChange({
                        ...component,
                        text: e.target.value,
                      })
                    }
                    placeholder={t('header_text_placeholder')}
                    className="border-base-300 focus:border-custom-primary-accent-600 focus:ring-custom-primary-accent-600 w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-1"
                  />
                </div>
              )}

              {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(component.format!) && (
                <div className="space-y-4">
                  {!component.media && (
                    <div>
                      <label className="text-base-content mb-2 block text-sm font-medium">
                        {t('upload_media', {
                          type: component.format?.toLowerCase(),
                        })}
                      </label>
                      <MediaUploader
                        onMediaUploaded={(media) =>
                          onComponentChange({ ...component, media })
                        }
                        acceptedTypes={
                          component.format === 'IMAGE'
                            ? ['image/jpeg', 'image/png', 'image/webp']
                            : component.format === 'VIDEO'
                              ? ['video/mp4', 'video/3gpp']
                              : ['application/pdf']
                        }
                        maxFileSize={
                          component.format === 'VIDEO'
                            ? 16 * 1024 * 1024
                            : 5 * 1024 * 1024
                        }
                        agentId={agentId}
                        channelId={channelId}
                        uploadedMedia={component.media}
                      />
                    </div>
                  )}

                  {component.media && (
                    <MediaPreview
                      media={component.media}
                      onRemove={() =>
                        onComponentChange({ ...component, media: undefined })
                      }
                    />
                  )}
                </div>
              )}

              <VariableManager
                variables={variables}
                onVariablesChange={onVariablesChange}
                selectedComponent="HEADER"
                t={t}
              />
            </>
          )}
        </div>
      );

    case 'BODY':
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base-content text-lg font-medium">
              {t('component_body')}
            </h3>
            <span className="text-sm text-red-600">* {t('required')}</span>
          </div>

          <div>
            <label className="text-base-content mb-2 block text-sm font-medium">
              {t('message_label')}
            </label>

            {/* Variable insertion buttons */}
            {componentVariables.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1">
                {componentVariables.map((variable) => (
                  <button
                    key={variable.id}
                    onClick={() => insertVariable(variable.position)}
                    className="bg-custom-primary-accent-100 hover:bg-custom-primary-accent-200 text-custom-primary-accent-700 rounded px-2 py-1 text-xs"
                  >
                    {`{${variable.position}} ${variable.name}`}
                  </button>
                ))}
              </div>
            )}

            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={component.bodyText || ''}
              onChange={(value) =>
                onComponentChange({
                  ...component,
                  bodyText: value,
                })
              }
              modules={modules}
              formats={formats}
              placeholder={t('message_placeholder')}
              className="border-base-300 bg-base-100 rounded-md border"
            />
            <p className="text-base-content/60 mt-1 text-xs">
              {t('message_requirements')}
            </p>
          </div>

          <VariableManager
            variables={variables}
            onVariablesChange={onVariablesChange}
            selectedComponent="BODY"
            t={t}
          />
        </div>
      );

    case 'FOOTER':
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base-content text-lg font-medium">
              {t('component_footer')}
            </h3>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={component.enabled}
                onChange={(e) =>
                  onComponentChange({ ...component, enabled: e.target.checked })
                }
                className="border-base-300 text-custom-primary-accent-600 focus:ring-custom-primary-accent-600 rounded"
              />
              <span className="text-base-content ml-2 text-sm">
                {t('enable')}
              </span>
            </label>
          </div>

          {component.enabled && (
            <>
              <div>
                <label className="text-base-content mb-2 block text-sm font-medium">
                  {t('footer_text')}
                </label>
                <input
                  type="text"
                  value={component.footerText || ''}
                  onChange={(e) =>
                    onComponentChange({
                      ...component,
                      footerText: e.target.value,
                    })
                  }
                  placeholder={t('footer_text_placeholder')}
                  className="border-base-300 focus:border-custom-primary-accent-600 focus:ring-custom-primary-accent-600 w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-1"
                />
              </div>

              <VariableManager
                variables={variables}
                onVariablesChange={onVariablesChange}
                selectedComponent="FOOTER"
                t={t}
              />
            </>
          )}
        </div>
      );

    case 'BUTTONS':
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base-content text-lg font-medium">
              {t('component_buttons')}
            </h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => {
                  const newButton = {
                    id: Date.now().toString(),
                    type: 'QUICK_REPLY' as const,
                    text: '',
                  };
                  const buttons = component.buttons || [];
                  if (buttons.length < 3) {
                    onComponentChange({
                      ...component,
                      enabled: true,
                      buttons: [...buttons, newButton],
                    });
                  }
                }}
                disabled={(component.buttons || []).length >= 3}
                className="text-custom-primary-accent-600 hover:text-custom-primary-accent-700 text-sm disabled:cursor-not-allowed disabled:opacity-50"
              >
                + {t('add_button')}
              </button>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={component.enabled}
                  onChange={(e) =>
                    onComponentChange({
                      ...component,
                      enabled: e.target.checked,
                    })
                  }
                  className="border-base-300 text-custom-primary-accent-600 focus:ring-custom-primary-accent-600 rounded"
                />
                <span className="text-base-content ml-2 text-sm">
                  {t('enable')}
                </span>
              </label>
            </div>
          </div>

          {component.enabled && (
            <>
              <div className="space-y-3">
                {(component.buttons || []).map((button) => (
                  <div
                    key={button.id}
                    className="border-custom-surface-200 space-y-3 rounded-lg border p-3"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium">
                          {t('button_number', {
                            number:
                              (component.buttons || []).indexOf(button) + 1,
                          })}
                        </span>
                      </div>
                      <button
                        onClick={() => {
                          const updatedButtons = (
                            component.buttons || []
                          ).filter((b) => b.id !== button.id);
                          onComponentChange({
                            ...component,
                            buttons: updatedButtons,
                          });
                        }}
                        className="text-red-500 hover:text-red-700"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>

                    <div>
                      <label className="text-base-content mb-1 block text-xs font-medium">
                        {t('button_type')}
                      </label>
                      <select
                        value={button.type}
                        onChange={(e) => {
                          const updatedButtons = (component.buttons || []).map(
                            (b) =>
                              b.id === button.id
                                ? {
                                    ...b,
                                    type: e.target.value as
                                      | 'QUICK_REPLY'
                                      | 'URL',
                                  }
                                : b,
                          );
                          onComponentChange({
                            ...component,
                            buttons: updatedButtons,
                          });
                        }}
                        className="border-base-300 focus:border-custom-primary-accent-600 w-full rounded border px-2 py-1 text-sm focus:outline-none"
                      >
                        <option value="QUICK_REPLY">
                          {t('type_quick_reply')}
                        </option>
                        <option value="URL">{t('type_url')}</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-base-content mb-1 block text-xs font-medium">
                        {t('button_text')}
                      </label>
                      <input
                        type="text"
                        value={button.text}
                        onChange={(e) => {
                          const updatedButtons = (component.buttons || []).map(
                            (b) =>
                              b.id === button.id
                                ? { ...b, text: e.target.value }
                                : b,
                          );
                          onComponentChange({
                            ...component,
                            buttons: updatedButtons,
                          });
                        }}
                        placeholder={t('button_text_placeholder')}
                        className="border-base-300 focus:border-custom-primary-accent-600 w-full rounded border px-2 py-1 text-sm focus:outline-none"
                      />
                    </div>

                    {button.type === 'URL' && (
                      <div>
                        <label className="text-base-content mb-1 block text-xs font-medium">
                          {t('url_destination')}
                        </label>
                        <input
                          type="url"
                          value={button.url || ''}
                          onChange={(e) => {
                            const updatedButtons = (
                              component.buttons || []
                            ).map((b) =>
                              b.id === button.id
                                ? { ...b, url: e.target.value }
                                : b,
                            );
                            onComponentChange({
                              ...component,
                              buttons: updatedButtons,
                            });
                          }}
                          placeholder={t('url_placeholder')}
                          className="border-base-300 focus:border-custom-primary-accent-600 w-full rounded border px-2 py-1 text-sm focus:outline-none"
                        />
                        <p className="text-base-content/60 mt-1 text-xs">
                          {t('url_help')}
                        </p>
                      </div>
                    )}

                    {button.type === 'QUICK_REPLY' && (
                      <div className="space-y-2">
                        <div className="rounded bg-blue-50 p-2">
                          <p className="text-xs text-blue-700">
                            {t('quick_reply_info')}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {(component.buttons || []).length === 0 && (
                <div className="border-base-300 rounded-lg border-2 border-dashed p-6 text-center">
                  <p className="text-base-content/60 text-sm">
                    {t('no_buttons_configured')}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      );

    default:
      return null;
  }
};

export const CreateTemplateModule = ({
  agentId,
  channel,
}: CreateTemplateModuleProps) => {
  const { t } = useTranslation('campaigns');
  const navigate = useNavigate();
  const { agentId: paramAgentId } = useParams();
  const { renderToast } = useLayoutContext();
  const location = useLocation();

  // Use selected channel from navigation state if available, otherwise fall back to prop
  const selectedChannelFromState = (location.state as any)?.selectedChannel;
  const selectedChannelId = (location.state as any)?.selectedChannelId;
  const targetChannel = selectedChannelFromState || channel;

  const [templateData, setTemplateData] = useState<TemplateData>({
    templateName: '',
    category: 'UTILITY',
    language: 'es',
    components: [
      { type: 'HEADER', enabled: false, format: 'TEXT' },
      { type: 'BODY', enabled: true, bodyText: '' },
      { type: 'FOOTER', enabled: false },
      { type: 'BUTTONS', enabled: false, buttons: [] },
    ],
    variables: [],
  });

  const [activeTab, setActiveTab] = useState<
    'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS'
  >('BODY');
  const [isCreating, setIsCreating] = useState(false);

  // Fetch integration data
  const { data: templatesData } = useQuery(LIST_WHATSAPP_TEMPLATES, {
    variables: {
      data: {
        agent_id: agentId,
      },
    },
  });

  

  const [createTemplate] = useMutation(CREATE_WHATSAPP_TEMPLATE, {
    onCompleted: (res) => {
      setIsCreating(false);
      const response = (res as any)?.createWhatsappTemplate;
      if (response?.hasError) {
        renderToast({
          message: response?.message || 'Error al crear la plantilla',
          type: 'error',
        });
        return;
      }
      renderToast({
        message:
          response?.message ||
          'Plantilla creada correctamente. Estado: pendiente de aprobación.',
        type: 'success',
      });
      // Volver al listado de plantillas
      navigate(`/agent/${paramAgentId}/campaigns/templates`);
    },
    onError: (error) => {
      setIsCreating(false);
      renderToast({
        message: error.message || 'Error al crear la plantilla',
        type: 'error',
      });
    },
  });

  const updateComponent = useCallback((updatedComponent: TemplateComponent) => {
    setTemplateData((prev) => {
      const updatedComponents = prev.components.map((c) =>
        c.type === updatedComponent.type ? updatedComponent : c,
      );

      return {
        ...prev,
        components: updatedComponents,
      };
    });
  }, []);

  const updateVariables = useCallback((variables: Variable[]) => {
    setTemplateData((prev) => ({ ...prev, variables }));
  }, []);

  const handleCreate = async () => {
    // Log the raw HTML coming from the body editor when the user clicks create
    const bodyHtml = templateData.components.find((c) => c.type === 'BODY')?.bodyText;
    
    if (
      !templateData.templateName ||
      !templateData.components.find((c) => c.type === 'BODY')?.bodyText
    ) {
      renderToast({
        message: 'Por favor completa los campos requeridos',
        type: 'warning',
      });
      return;
    }

    setIsCreating(true);

    const components: any[] = [];

    templateData.components.forEach((component) => {
      if (!component.enabled && component.type !== 'BODY') return;

      switch (component.type) {
        case 'HEADER':
          // Only include header if it has valid content
          if (component.format === 'TEXT' && component.text?.trim()) {
            const headerComponent = {
              type: 'HEADER',
              format: 'TEXT',
              text: component.text.trim(),
            };
            components.push(headerComponent);
            console.log('Header component added:', headerComponent);
          } else if (
            component.media &&
            component.media.meta_info?.file_handle &&
            ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(component.format!)
          ) {
            // Include media header with file handle from Meta API
            const headerComponent = {
              type: 'HEADER',
              format: component.format,
              example: {
                header_handle: [component.media.meta_info.file_handle],
              },
            };
            components.push(headerComponent);
            console.log('Header media component added:', headerComponent);
          }
          // If header is enabled but has no valid content, skip it
          break;

        case 'BODY':
          // Body text with WhatsApp formatting support
          const convertedText = convertHtmlToWhatsApp(component.bodyText) || '';
          const bodyComponent = {
            type: 'BODY',
            text: convertedText,
          };
          components.push(bodyComponent);
          console.log('Body component added:', bodyComponent);
          console.log('HTML to WhatsApp conversion result:', convertedText);
          break;

        case 'FOOTER':
          if (component.footerText?.trim()) {
            const footerComponent = {
              type: 'FOOTER',
              text: component.footerText.trim(),
            };
            components.push(footerComponent);
            console.log('Footer component added:', footerComponent);
          }
          break;

        case 'BUTTONS':
          const validButtons = (component.buttons || []).filter(
            (button) =>
              button.text?.trim() &&
              (button.type !== 'URL' || button.url?.trim()),
          );

          if (validButtons.length > 0) {
            const buttonsComponent = {
              type: 'BUTTONS',
              buttons: validButtons.map((button) => ({
                type: button.type,
                text: button.text.trim(),
                ...(button.type === 'URL' && { url: button.url?.trim() }),
              })),
            };
            components.push(buttonsComponent);
            console.log('Buttons component added:', buttonsComponent);
          }
          break;
      }
    });

    const templateRequestData = {
      channel_id: targetChannel._id,
      agent_id: agentId,
      template_name: templateData.templateName,
      language: templateData.language,
      category: templateData.category,
      template_use: 'CAMPAING',
      components,
      variables: templateData.variables.map((v) => ({
        name: v.name,
        position: v.position,
        example: v.example,
        component_type: v.component_type,
      })),
    };

    // console.log('=== FINAL TEMPLATE DATA THAT WILL BE SENT ===');
    // console.log('Complete template request data:', JSON.stringify(templateRequestData, null, 2));
    // console.log('All components to be sent:', components);
    // console.log('Variables to be sent:', templateRequestData.variables);
    // console.log('=== END DEBUG ===');

    await createTemplate({
      variables: {
        data: templateRequestData,
      },
    });
  };

  const goBack = () => {
    navigate(`/agent/${paramAgentId}/campaigns/templates`);
  };

  const isValidForCreation = () => {
    const hasTemplateName = templateData.templateName?.trim();
    const hasBodyText = templateData.components
      .find((c) => c.type === 'BODY')
      ?.bodyText?.trim();

    // Check if header is enabled but invalid
    const headerComponent = templateData.components.find(
      (c) => c.type === 'HEADER',
    );
    const hasInvalidHeader =
      headerComponent?.enabled &&
      ((headerComponent.format === 'TEXT' && !headerComponent.text?.trim()) ||
        (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerComponent.format!) &&
          !headerComponent.media?.meta_info?.file_handle));

    // Check if buttons are enabled but invalid
    const buttonsComponent = templateData.components.find(
      (c) => c.type === 'BUTTONS',
    );
    const hasInvalidButtons =
      buttonsComponent?.enabled &&
      (!buttonsComponent.buttons?.length ||
        buttonsComponent.buttons.some(
          (button) =>
            !button.text?.trim() ||
            (button.type === 'URL' && !button.url?.trim()),
        ));

    return (
      hasTemplateName && hasBodyText && !hasInvalidHeader && !hasInvalidButtons
    );
  };

  const getValidationMessage = () => {
    if (!templateData.templateName?.trim()) {
      return 'El nombre de la plantilla es requerido';
    }

    if (
      !templateData.components.find((c) => c.type === 'BODY')?.bodyText?.trim()
    ) {
      return 'El cuerpo del mensaje es requerido';
    }

    const headerComponent = templateData.components.find(
      (c) => c.type === 'HEADER',
    );
    if (headerComponent?.enabled) {
      if (headerComponent.format === 'TEXT' && !headerComponent.text?.trim()) {
        return 'El encabezado de texto no puede estar vacío';
      }
      if (
        ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerComponent.format!) &&
        !headerComponent.media?.meta_info?.file_handle
      ) {
        return 'Debes subir un archivo para el encabezado de medios';
      }
    }

    const buttonsComponent = templateData.components.find(
      (c) => c.type === 'BUTTONS',
    );
    if (buttonsComponent?.enabled) {
      if (!buttonsComponent.buttons?.length) {
        return 'Debes agregar al menos un botón';
      }

      const invalidButton = buttonsComponent.buttons.find(
        (button) =>
          !button.text?.trim() ||
          (button.type === 'URL' && !button.url?.trim()),
      );

      if (invalidButton) {
        return 'Todos los botones deben tener texto y URLs válidas (si aplica)';
      }
    }

    return null;
  };

  return (
    <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 lg:grid-cols-2">
      {/* Editor Section */}
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center space-x-4">
          <button
            onClick={goBack}
            className="border-base-300 hover:bg-base-100 flex items-center space-x-2 rounded-lg border px-3 py-2 text-sm transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            <span>{t('back_to_templates')}</span>
          </button>
          <h1 className="text-base-content text-2xl font-bold">
            {t('template_create_title')}
          </h1>
        </div>

        {/* Basic Information */}
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <h2 className="card-title text-base-content">
              {t('template_basic_info')}
            </h2>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-base-content mb-2 block text-sm font-medium">
                  {t('template_name')} *
                </label>
                <input
                  type="text"
                  value={templateData.templateName}
                  onChange={(e) =>
                    setTemplateData((prev) => ({
                      ...prev,
                      templateName: e.target.value,
                    }))
                  }
                  placeholder="mi_plantilla_promocional"
                  className="border-base-300 focus:border-custom-primary-accent-600 focus:ring-custom-primary-accent-600 w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-1"
                />
              </div>

              <div>
                <label className="text-base-content mb-2 block text-sm font-medium">
                  {t('template_category')}
                </label>
                <select
                  value={templateData.category}
                  onChange={(e) =>
                    setTemplateData((prev) => ({
                      ...prev,
                      category: e.target.value as any,
                    }))
                  }
                  className="border-base-300 focus:border-custom-primary-accent-600 focus:ring-custom-primary-accent-600 w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-1"
                >
                  <option value="UTILITY">{t('category_utility')}</option>
                  <option value="MARKETING">{t('category_marketing')}</option>
                  <option value="AUTHENTICATION">
                    {t('category_authentication')}
                  </option>
                </select>
              </div>

              <div>
                <label className="text-base-content mb-2 block text-sm font-medium">
                  {t('template_language')}
                </label>
                <select
                  value={templateData.language}
                  onChange={(e) =>
                    setTemplateData((prev) => ({
                      ...prev,
                      language: e.target.value,
                    }))
                  }
                  className="border-base-300 focus:border-custom-primary-accent-600 focus:ring-custom-primary-accent-600 w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-1"
                >
                  <option value="es">{t('language_spanish')}</option>
                  <option value="en">{t('language_english')}</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Component Tabs */}
        <div className="card bg-base-100 shadow-xl">
          <div className="border-custom-surface-200 border-b">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              {(['HEADER', 'BODY', 'FOOTER', 'BUTTONS'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium ${
                    activeTab === tab
                      ? 'border-custom-primary-accent-500 text-custom-primary-accent-600'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }`}
                >
                  {tab === 'HEADER' && t('component_header')}
                  {tab === 'BODY' && `${t('component_body')} *`}
                  {tab === 'FOOTER' && t('component_footer')}
                  {tab === 'BUTTONS' && t('component_buttons')}
                  {templateData.components.find((c) => c.type === tab)
                    ?.enabled && (
                    <span className="ml-2 inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                      ✓
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            <ComponentEditor
              component={
                templateData.components.find((c) => c.type === activeTab)!
              }
              onComponentChange={updateComponent}
              variables={templateData.variables}
              onVariablesChange={updateVariables}
              agentId={agentId}
              channelId={targetChannel._id}
              setActiveTab={setActiveTab}
              t={t}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            onClick={goBack}
            className="border-base-300 hover:bg-base-100 rounded-lg border px-4 py-2 text-sm transition-colors"
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleCreate}
            disabled={!isValidForCreation() || isCreating}
            className="btn btn-primary disabled:opacity-50"
          >
            {isCreating ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                {t('creating_template')}
              </>
            ) : (
              t('create_template')
            )}
          </button>
        </div>

        {/* Validation Warning */}
        {!isValidForCreation() && (
          <div className="alert alert-warning">
            <ExclamationTriangleIcon className="h-5 w-5" />
            <span>{getValidationMessage()}</span>
          </div>
        )}
      </div>

      {/* Preview Section */}
      <div className="sticky top-6">
        <WhatsAppPreview templateData={templateData} />
      </div>
    </div>
  );
};
